/**
 * Copyright 2026 Perform Content Services Limited. Confidential and Proprietary. All Rights Reserved.
 */
import moment from 'moment';
import * as _ from 'phenix-web-lodash-light';

import PhenixWebSocket, {requestType} from 'services/websocket.provider';
import fetch from 'services/fetch.service';
import urlPath from 'services/url-path';
import userStore from 'services/user-store';
import {auth} from 'services/auth.service';
import {toSeconds, formatNumberWithPrecision} from 'utility/numbers';
import {PortalError} from 'utility/error-handler';
import {
  ITimeToFirstFrameCluster,
  ITimeToFirstFrameData,
  IUsageStatistics,
  IUsageStatisticsByType,
  IUsageStatisticsChartData,
  IUsageStatisticsGeneral,
  IActiveUserData,
  IReport,
  IUsageStatisticsValue
} from 'interfaces/analyticsProps';
import {IUsageSummaryGroup} from 'views/analytics/summary';

export const videoType = {
  realTime: 'RealTime',
  dash: 'Dash',
  hls: 'Hls'
};
const oneMinuteInMilliseconds = moment.duration(1, 'minute').asMilliseconds();

export interface ICurrentUsageStatistics {
  continuationId?: string | null;
  routeKey?: string | null;
  status: string;
  usage: IUsageStatisticsValue;
  usageByType: IUsageStatisticsByType[];
  usageByCountry: {
    continent?: string;
    country: string;
    usageByType: IUsageStatisticsByType[];
  }[];
}

interface IFilterByGroup {
  groupName: string;
  users: number;
  sessions: number;
}

export interface IActiveUsersData {
  byCity: IFilterByGroup[];
  byCountry: IFilterByGroup[];
  byManufacturer: IFilterByGroup[];
  byPlatform: IFilterByGroup[];
  continuationId: string | null;
  routeKey: string | null;
  status: string;
  users: number;
}

export const fetchTimeToFirstFrame = async(
  socket: PhenixWebSocket, start: number, end: number, applicationId: string, kind: string
):Promise<Array<number>> => {
  if (!socket.isAuthenticated() || !applicationId) {
    throw new PortalError('socket-not-authenticated', {statusCode: 401});
  }

  const requestPayload = {
    start,
    end,
    kind,
    applicationIds: [applicationId]
  };
  const response = await socket.sendRequest(requestType.timeToFirstFrame, requestPayload);

  if (response?.status === 'ok' && response.cdf && typeof response.cdf.data === 'object') {
    return response.cdf.data;
  }

  throw new PortalError('unable-to-fetch-data', {
    statusCode: response.status,
    requestPayload: JSON.stringify({
      method: requestType.timeToFirstFrame,
      data: requestPayload
    })
  });
};

export const getChartData = (data: number[]): Array<ITimeToFirstFrameData> => {
  return data.map((value, index) => {
    return {
      percentile: index / 10,
      value: toSeconds(value)
    };
  });
};

export const computeMedian = (data: number[]): number => {
  const {length} = data;

  if (length === 0) {
    return 0;
  }

  if (length === 1) {
    return data[0];
  }

  return length % 2 === 0
    ? (data[(length / 2) - 1] + data[length / 2]) / 2
    : data[(length - 1) / 2];
};

export const findTimeToFirstFrameAtPercentage = (data: number[], percentage: number): number => {
  const index = Math.floor(data.length * percentage / 100);

  return data[index];
};

export const fetchAllTimeToFirstFrame = async(socket: PhenixWebSocket, start: number, end: number, applicationId: string): Promise<ITimeToFirstFrameCluster> => {
  if (!socket.isAuthenticated() || !applicationId) {
    throw new PortalError('socket-not-authenticated', {statusCode: 401});
  }

  const [realTimeData, dashData, hlsData] = await Promise.all([
    fetchTimeToFirstFrame(socket, start, end, applicationId, videoType.realTime),
    fetchTimeToFirstFrame(socket, start, end, applicationId, videoType.dash),
    fetchTimeToFirstFrame(socket, start, end, applicationId, videoType.hls)
  ]);
  const chartData = {
    realTime: {
      data: getChartData(realTimeData),
      median: computeMedian(realTimeData),
      timeToFirstFrame90Percentile: findTimeToFirstFrameAtPercentage(realTimeData, 90)
    },
    dash: {
      data: getChartData(dashData),
      median: computeMedian(dashData),
      timeToFirstFrame90Percentile: findTimeToFirstFrameAtPercentage(dashData, 90)
    },
    hls: {
      data: getChartData(hlsData),
      median: computeMedian(hlsData),
      timeToFirstFrame90Percentile: findTimeToFirstFrameAtPercentage(hlsData, 90)
    }
  };

  return chartData;
};

export const defaultViewingStreamValue = {
  type: 'destination',
  subtype: 'leaf',
  usage: {
    streams: 0,
    users: 0,
    devices: 0,
    minutes: 0,
    minutesPerStream: 0,
    minutesPerUser: 0
  },
  minutesPerView: 0,
  minutesPerUser: 0
};

export const filterUsageStatisticsData = (currentUsageStatistics: ICurrentUsageStatistics): Array<IUsageStatisticsChartData> => {
  if (!currentUsageStatistics.usageByCountry) {
    return [];
  }

  const countriesStatistics = [];

  currentUsageStatistics.usageByCountry.forEach(currentUsage => {
    // See if the country in this "currentUsage" matches a country in "countriesStatistics"
    const indexOfMatchingCountry = countriesStatistics.findIndex(countryStats => countryStats.country === currentUsage.country);

    if (indexOfMatchingCountry > -1) {
      const usageDataOfMatchingCountry = countriesStatistics[indexOfMatchingCountry].data;

      currentUsage.usageByType.forEach(currentUsageProp => {
        // See if the matching country has a usage prop of same "type" as the current usage prop
        const indexOfMatchingProp = usageDataOfMatchingCountry.findIndex(prop => prop.type === currentUsageProp.type);

        if (indexOfMatchingProp > -1) {
          const matchingProp = usageDataOfMatchingCountry[indexOfMatchingProp];

          matchingProp.usage = {
            devices: matchingProp.usage.devices + (currentUsageProp.usage.devices || 0),
            minutes: matchingProp.usage.minutes + (currentUsageProp.usage.minutes || 0),
            streams: matchingProp.usage.streams + (currentUsageProp.usage.streams || 0),
            users: matchingProp.usage.users + (currentUsageProp.usage.users || 0)
          };
        } else {
          usageDataOfMatchingCountry.push(currentUsageProp);
        }
      });
    } else {
      countriesStatistics.push({
        country: currentUsage.country,
        data: currentUsage.usageByType
      });
    }
  });

  return countriesStatistics;
};

export const findViewingStreamUsage = (usageStatistics: ICurrentUsageStatistics): IUsageStatisticsByType => {
  const viewingStreamUsage = (usageStatistics.usageByType && usageStatistics.usageByType.filter(usage => usage.type === 'destination' && usage.subtype === 'leaf')[0])
    || defaultViewingStreamValue;

  viewingStreamUsage.minutesPerView = viewingStreamUsage.usage.streams !== 0
    ? viewingStreamUsage.usage.minutes / viewingStreamUsage.usage.streams : 0;

  viewingStreamUsage.minutesPerUser = viewingStreamUsage.usage.users !== 0
    ? viewingStreamUsage.usage.minutes / viewingStreamUsage.usage.users : 0;

  return viewingStreamUsage;
};

export const generateDefaultUsageData = (keys: string[]): IUsageSummaryGroup => {
  const data = {} as IUsageSummaryGroup;

  keys.forEach((key: string) => {
    data[key] = {
      publishing: {
        streams: '',
        users: '',
        devices: '',
        minutes: ''
      },
      viewing: {
        streams: '',
        users: '',
        devices: '',
        minutes: ''
      }
    };
  });

  return data;
};

export const defaultUsageData = generateDefaultUsageData(['usageLast24Hours', 'usagePreLast24Hours', 'usageLast30Days', 'usagePreLast30Days']);

export const filterUsageByType = (usageResponse: ICurrentUsageStatistics): IUsageStatisticsGeneral => {
  const publishingUsage = usageResponse.usageByType.filter(data => data.type === 'source' && data.subtype === 'root')[0] || {};
  const viewingUsage = usageResponse.usageByType.filter(data => data.type === 'destination' && data.subtype === 'leaf')[0] || {};

  return {
    publishing: {
      streams: _.get(publishingUsage, ['usage', 'streams'], 0),
      users: _.get(publishingUsage, ['usage', 'users'], 0),
      devices: _.get(publishingUsage, ['usage', 'devices'], 0),
      minutes: _.get(publishingUsage, ['usage', 'minutes'], 0)
    },
    viewing: {
      streams: _.get(viewingUsage, ['usage', 'streams'], 0),
      users: _.get(viewingUsage, ['usage', 'users'], 0),
      devices: _.get(viewingUsage, ['usage', 'devices'], 0),
      minutes: _.get(viewingUsage, ['usage', 'minutes'], 0)
    }
  };
};

interface IUsageByType {
  publishing: {
    devices: number;
    minutes: number;
    minutesPerStream: number;
    minutesPerUser: number;
    streams: number;
    users: number;
  };
  viewing: {
    devices: number;
    minutes: number;
    minutesPerStream: number;
    minutesPerUser: number;
    streams: number;
    users: number;
  };
}

export const getUsageByType = ({usageByType}: {usageByType: IUsageStatisticsByType[]}): IUsageByType => {
  const publishingData = usageByType.filter(data => data.type === 'source' && data.subtype === 'root')[0] || {};
  const viewingData = usageByType.filter(data => data.type === 'destination' && data.subtype === 'leaf')[0] || {};

  return {
    publishing: {
      users: _.get(publishingData, ['usage', 'users'], 0),
      streams: _.get(publishingData, ['usage', 'streams'], 0),
      minutesPerUser: _.get(publishingData, ['usage', 'minutes'], 0) / _.get(publishingData, ['usage', 'users'], 0),
      minutesPerStream: _.get(publishingData, ['usage', 'minutes'], 0) / _.get(publishingData, ['usage', 'streams'], 0),
      minutes: _.get(publishingData, ['usage', 'minutes'], 0),
      devices: _.get(publishingData, ['usage', 'devices'], 0)
    },
    viewing: {
      users: _.get(viewingData, ['usage', 'users'], 0),
      streams: _.get(viewingData, ['usage', 'streams'], 0),
      minutesPerUser: _.get(viewingData, ['usage', 'minutes'], 0) / _.get(viewingData, ['usage', 'users'], 0),
      minutesPerStream: _.get(viewingData, ['usage', 'minutes'], 0) / _.get(viewingData, ['usage', 'streams'], 0),
      minutes: _.get(viewingData, ['usage', 'minutes'], 0),
      devices: _.get(viewingData, ['usage', 'devices'], 0)
    }
  };
};

export const fetchUsageStatistics = async(socket: PhenixWebSocket, start: number, end: number, applicationId: string): Promise<ICurrentUsageStatistics> => {
  if (!socket.isAuthenticated() || !applicationId) {
    throw new PortalError('socket-not-authenticated', {statusCode: 401});
  }

  const requestPayload = {
    start: start,
    end: end,
    applicationIds: [applicationId]
  };
  const response = await socket.sendRequest(requestType.geographicUsageStatistics, requestPayload);

  if (response?.status !== 'ok') {
    throw new PortalError('unable-to-fetch-data', {
      statusCode: response.status,
      requestPayload: JSON.stringify({
        method: requestType.geographicUsageStatistics,
        data: requestPayload
      })
    });
  }

  return response;
};

export const fetchAllUsageStatistics = async(socket: PhenixWebSocket, start: number, end: number, applicationId: string): Promise<IUsageStatistics> => {
  if (!socket.isAuthenticated() || !applicationId) {
    throw new PortalError('socket-not-authenticated', {statusCode: 401});
  }

  const range = moment.utc(end).diff(start);
  const pastStartDate = moment.utc(start).subtract(range, 'milliseconds');
  const [currentUsageStatistics, pastUsageStatistics] = await Promise.all([
    fetchUsageStatistics(socket, start, end, applicationId),
    fetchUsageStatistics(socket, pastStartDate.valueOf(), start, applicationId)
  ]);
  const usageStatisticsChartData = filterUsageStatisticsData(currentUsageStatistics);
  const currentViewStreamUsage = findViewingStreamUsage(currentUsageStatistics);
  const pastViewStreamUsage = findViewingStreamUsage(pastUsageStatistics);
  const currentUsageStatisticsByType = getUsageByType(currentUsageStatistics);
  const pastUsageStatisticsByType = getUsageByType(pastUsageStatistics);

  return {
    usageStatisticsChartData,
    currentViewStreamUsage,
    pastViewStreamUsage,
    currentUsageStatisticsByType,
    pastUsageStatisticsByType
  };
};

export const fetchActiveUsers = async(socket: PhenixWebSocket, snapshotTime: number, applicationId: string): Promise<IActiveUsersData> => {
  if (!socket.isAuthenticated() || !applicationId) {
    throw new PortalError('socket-not-authenticated', {statusCode: 401});
  }

  const requestPayload = {
    snapshotTime,
    applicationIds: [applicationId]
  };
  const response = await socket.sendRequest(requestType.activeUsers, requestPayload);

  if (response?.status !== 'ok') {
    throw new PortalError('unable-to-fetch-data', {
      statusCode: response.status,
      requestPayload: JSON.stringify({
        method: requestType.activeUsers,
        data: requestPayload
      })
    });
  }

  if (!response.byCountry) {
    throw new PortalError('by-country-missing', {
      statusCode: response.status,
      requestPayload: JSON.stringify({
        method: requestType.activeUsers,
        data: requestPayload
      })
    });
  }

  return response;
};

export const filterActiveUserData = (response: IActiveUsersData): Array<IActiveUserData> => {
  if (!Array.isArray(response.byCountry)) {
    return [];
  }

  const chartData = response.byCountry.map(active => ({
    country: active.groupName,
    data: {
      users: active.users,
      sessions: active.sessions
    }
  }));

  return chartData.sort((countryAData, countryBData) => {
    if (countryAData.data === undefined || countryBData.data === undefined) {
      return 0;
    }

    return (countryAData.data.users + countryAData.data.sessions) <
        (countryBData.data.users + countryBData.data.sessions) ? 1 : -1;
  });
};

export const computeUsagePercentage = (currentValue: number, pastValue: number): string => {
  if (pastValue === 0) {
    if (currentValue > 0) {
      return '+100%';
    } else if (currentValue < 0) {
      return '-100%';
    }

    return '0%';
  }

  const difference = currentValue - pastValue;
  const sign = difference > 0 ? '+' : (difference < 0) ? '-' : '';
  const answer = formatNumberWithPrecision(Math.abs(difference) * 100 / pastValue, 1);

  return `${sign}${answer}%`;
};

export const getReports = async(data: {[key: string]: IReport}, kind: string): Promise<{data: string; headers: Headers}> => {
  const headers = new Headers();

  headers.set('Authorization', `Basic ${await userStore.get(auth.token)}`);

  return fetch.put({
    url: `${(kind === 'Publishing') ? urlPath.publishReporting : urlPath.viewReporting}`,
    data,
    headers,
    timeout: oneMinuteInMilliseconds,
    retryConfig: {maxRetries: 3}
  });
};

export const getForkReports = async(data: IReport): Promise<string[]> => {
  const headers = new Headers();

  headers.set('Authorization', `Basic ${await userStore.get(auth.token)}`);

  const [sourceChannelsResponse, destinationChannelsResponse] = await Promise.all([
    fetch.put({
      url: urlPath.forkHistoryReporting,
      data: {
        forkHistoryReport: {
          start: data.start,
          end: data.end,
          sourceChannelIds: data.channelIds
        }
      },
      headers,
      timeout: oneMinuteInMilliseconds
    }),
    fetch.put({
      url: urlPath.forkHistoryReporting,
      data: {
        forkHistoryReport: {
          start: data.start,
          end: data.end,
          destinationChannelIds: data.channelIds
        }
      },
      headers,
      timeout: oneMinuteInMilliseconds
    })
  ]);

  return [sourceChannelsResponse.data, destinationChannelsResponse.data];
};

export const getConcurrentViewers = async(data: {[key: string]: string | Date}): Promise<string> => {
  const {id, start, end} = data;
  const headers = new Headers();

  headers.set('Authorization', `Basic ${await userStore.get(auth.token)}`);

  const response = await fetch.put({
    url: urlPath.concurrentViewers,
    data: {
      concurrentViewingStreamsReport: {
        channelIds: [id],
        start,
        end
      }
    },
    headers,
    timeout: oneMinuteInMilliseconds,
    retryConfig: {maxRetries: 3}
  });

  return response.data;
};

export const getIngestReports = async(data: {[key: string]: Date | string}): Promise<string> => {
  const {id, start, end} = data;
  const headers = new Headers();

  headers.set('Authorization', `Basic ${await userStore.get(auth.token)}`);

  const response = await fetch.put({
    url: urlPath.ingestReports,
    data: {
      ingestReport: {
        channelIds: [id],
        kind: 'BufferUnderrun',
        start,
        end
      }
    },
    headers,
    timeout: oneMinuteInMilliseconds,
    retryConfig: {maxRetries: 3}
  });

  return response.data;
};